import { offlineDB, OutboxRow, OutboxPriority, ConflictRow } from './db'
import { apiClient, getErrorMessage } from '@/lib/apiClient'
import { authService } from '@/services/auth/authService'
import { getLanguageLocale } from '@/lib/locale'
import type { SyncManifestEntity } from '@/hooks/useSyncManifest'

// ─── Types ────────────────────────────────────────────────────────────────────

export type SyncEventType =
  | 'sync:start'
  | 'sync:complete'
  | 'sync:error'
  | 'sync:conflict'
  | 'sync:flush-start'
  | 'sync:flush-complete'
  | 'outbox:added'
  | 'outbox:processed'

export interface SyncEvent {
  type: SyncEventType
  entity?: string
  detail?: unknown
}

type SyncListener = (event: SyncEvent) => void

interface IncrementalSyncResponse<T = unknown> {
  items: T[]
  deletedIds: string[]
  serverTime: string
}

// ─── Constants ────────────────────────────────────────────────────────────────

const MAX_RETRY_ATTEMPTS = 5
const PERIODIC_SYNC_INTERVAL = 60_000 // 60 seconds
const BACKOFF_BASE_MS = 1_000

/** Priority ordering for outbox flush (lower = first) */
const PRIORITY_ORDER: Record<OutboxPriority, number> = {
  high: 0,
  normal: 1,
  low: 2,
}

// ─── Module State ─────────────────────────────────────────────────────────────

let listeners: SyncListener[] = []
let isSyncing = false
let periodicInterval: ReturnType<typeof setInterval> | null = null

// ─── Event System ─────────────────────────────────────────────────────────────

export function onSyncEvent(listener: SyncListener): () => void {
  listeners.push(listener)
  return () => {
    listeners = listeners.filter(l => l !== listener)
  }
}

function emit(event: SyncEvent): void {
  for (const listener of listeners) {
    try {
      listener(event)
    } catch {
      // Listener errors must not break the sync engine
    }
  }
}

// ─── Outbox Operations ────────────────────────────────────────────────────────

/**
 * Queue an offline mutation in the outbox.
 */
export async function queueMutation(
  row: Pick<OutboxRow, 'op' | 'entity' | 'entityId' | 'key' | 'payload' | 'etag' | 'baseUrl'> & {
    priority?: OutboxPriority
  }
): Promise<void> {
  const now = Date.now()
  await offlineDB.outbox.add({
    ...row,
    priority: row.priority ?? 'normal',
    attempts: 0,
    maxAttempts: MAX_RETRY_ATTEMPTS,
    lastAttempt: null,
    error: null,
    clientCreatedAt: now,
    clientModifiedAt: now,
    createdAt: now,
  })
  emit({ type: 'outbox:added', entity: row.entity })
}

/**
 * Flush the outbox in priority order: high → normal → low, then FIFO within each tier.
 * Uses exponential backoff for failed rows.
 */
export async function flushOutbox(): Promise<{ processed: number; failed: number; conflicts: number }> {
  if (!navigator.onLine) {
    return { processed: 0, failed: 0, conflicts: 0 }
  }

  emit({ type: 'sync:flush-start' })

  const rows = await offlineDB.outbox
    .orderBy('[priority+createdAt]')
    .toArray()

  // Sort by priority order (compound index may not enforce enum ordering)
  rows.sort((a, b) => {
    const pa = PRIORITY_ORDER[a.priority] ?? 1
    const pb = PRIORITY_ORDER[b.priority] ?? 1
    if (pa !== pb) return pa - pb
    return a.createdAt - b.createdAt
  })

  let processed = 0
  let failed = 0
  let conflicts = 0

  for (const row of rows) {
    // Skip rows that have exceeded max attempts
    if (row.attempts >= row.maxAttempts) {
      continue
    }

    // Check backoff: don't retry too soon
    if (row.lastAttempt) {
      const backoffMs = BACKOFF_BASE_MS * Math.pow(2, row.attempts - 1)
      if (Date.now() - row.lastAttempt < backoffMs) {
        continue
      }
    }

    try {
      const response = await executeOutboxRow(row)

      if (response.ok) {
        await offlineDB.outbox.delete(row.id!)
        processed++
        emit({ type: 'outbox:processed', entity: row.entity, detail: { id: row.entityId } })
      } else if (response.status === 409) {
        // Conflict — store for manual resolution
        const serverData = await response.json().catch(() => null)
        await storeConflict(row, serverData)
        await offlineDB.outbox.delete(row.id!)
        conflicts++
        emit({ type: 'sync:conflict', entity: row.entity, detail: { id: row.entityId } })
      } else {
        // Server error — increment attempts
        await offlineDB.outbox.update(row.id!, {
          attempts: row.attempts + 1,
          lastAttempt: Date.now(),
          error: `HTTP ${response.status}: ${response.statusText}`,
        })
        failed++
      }
    } catch (error) {
      // Network error — stop flushing (we're probably offline)
      if (!navigator.onLine) {
        break
      }
      await offlineDB.outbox.update(row.id!, {
        attempts: row.attempts + 1,
        lastAttempt: Date.now(),
        error: error instanceof Error ? error.message : 'Network error',
      })
      failed++
    }
  }

  emit({ type: 'sync:flush-complete', detail: { processed, failed, conflicts } })
  return { processed, failed, conflicts }
}

/**
 * Execute a single outbox row against the server API.
 */
async function executeOutboxRow(row: OutboxRow): Promise<Response> {
  const relative = row.baseUrl ?? `/api/${row.entity.toLowerCase()}`
  // Resolve relative URLs against apiClient's baseURL so outbox POSTs hit the
  // API host, not the SPA origin (Static Web Apps doesn't proxy /api/*).
  const apiOrigin = (apiClient.defaults.baseURL ?? '').replace(/\/$/, '')
  const baseUrl = /^https?:/i.test(relative) ? relative : `${apiOrigin}${relative}`
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'Accept-Language': getLanguageLocale(),
  }
  const token = authService.getToken()
  if (token) {
    headers['Authorization'] = `Bearer ${token}`
  }
  const tenantId = authService.getTenantId()
  if (tenantId) {
    headers['__tenant'] = tenantId
  }
  const legalEntityId = authService.getLegalEntityId()
  if (legalEntityId) {
    headers['X-Nexo-Legal-Entity-Id'] = legalEntityId
  }
  if (row.etag) {
    headers['If-Match'] = row.etag
  }
  // Pass client timestamps for audit logging
  if (row.payload && typeof row.payload === 'object') {
    ;(row.payload as Record<string, unknown>).clientCreatedAt = new Date(row.clientCreatedAt).toISOString()
    ;(row.payload as Record<string, unknown>).clientModifiedAt = new Date(row.clientModifiedAt).toISOString()
  }

  switch (row.op) {
    case 'POST':
      return fetch(baseUrl, { method: 'POST', headers, body: JSON.stringify(row.payload) })
    case 'PUT':
      return fetch(`${baseUrl}/${row.entityId}`, { method: 'PUT', headers, body: JSON.stringify(row.payload) })
    case 'PATCH':
      return fetch(`${baseUrl}/${row.entityId}`, { method: 'PATCH', headers, body: JSON.stringify(row.payload) })
    case 'DELETE':
      return fetch(`${baseUrl}/${row.entityId}`, { method: 'DELETE', headers })
    default:
      throw new Error(`Unknown outbox operation: ${row.op}`)
  }
}

// ─── Conflict Management ──────────────────────────────────────────────────────

async function storeConflict(row: OutboxRow, serverData: unknown): Promise<void> {
  const conflict: ConflictRow = {
    entity: row.entity,
    entityId: row.entityId,
    localData: row.payload,
    serverData,
    outboxId: row.id ?? null,
    diffFields: computeDiffFields(row.payload, serverData),
    createdAt: Date.now(),
    resolvedAt: null,
    resolution: null,
  }
  await offlineDB.conflicts.add(conflict)
}

function computeDiffFields(local: unknown, server: unknown): string[] {
  if (!local || !server || typeof local !== 'object' || typeof server !== 'object') {
    return []
  }
  const localObj = local as Record<string, unknown>
  const serverObj = server as Record<string, unknown>
  const allKeys = new Set([...Object.keys(localObj), ...Object.keys(serverObj)])
  const diffs: string[] = []
  for (const key of allKeys) {
    if (JSON.stringify(localObj[key]) !== JSON.stringify(serverObj[key])) {
      diffs.push(key)
    }
  }
  return diffs
}

export async function resolveConflict(
  conflictId: number,
  resolution: 'local' | 'server' | 'merged',
  mergedData?: unknown
): Promise<void> {
  const conflict = await offlineDB.conflicts.get(conflictId)
  if (!conflict) return

  if (resolution === 'local' || resolution === 'merged') {
    const data = resolution === 'merged' ? mergedData : conflict.localData
    await queueMutation({
      op: 'PUT',
      entity: conflict.entity,
      entityId: conflict.entityId,
      key: conflict.entityId,
      payload: data,
      priority: 'high',
    })
  }
  // 'server' resolution: accept server version — no action needed

  await offlineDB.conflicts.update(conflictId, {
    resolvedAt: Date.now(),
    resolution,
  })
}

export async function getUnresolvedConflicts(): Promise<ConflictRow[]> {
  return offlineDB.conflicts
    .filter(c => c.resolvedAt === null)
    .toArray()
}

// ─── Incremental Sync ─────────────────────────────────────────────────────────

/**
 * Perform incremental sync for a single entity.
 * Calls GET /api/sync/{entity}?modifiedAfter={datetime} to fetch changes since last sync.
 */
export async function incrementalSync(entityName: string): Promise<void> {
  const meta = await offlineDB.syncMeta.get(entityName)
  const modifiedAfter = meta?.lastSyncTime ?? ''

  try {
    const url = modifiedAfter
      ? `/api/sync/${entityName.toLowerCase()}?modifiedAfter=${encodeURIComponent(modifiedAfter)}`
      : `/api/sync/${entityName.toLowerCase()}`

    const response = await apiClient.get<IncrementalSyncResponse>(url)
    const { items, deletedIds, serverTime } = response.data

    // Apply changes to Dexie entity table (if it exists in generated schema)
    // This will be wired up in Phase 4 when services become offline-aware
    // For now, store sync metadata

    await offlineDB.syncMeta.put({
      entity: entityName,
      lastSyncTime: serverTime,
      lastFullSyncTime: meta?.lastFullSyncTime ?? (modifiedAfter ? null : serverTime),
      rowCount: (meta?.rowCount ?? 0) + items.length - deletedIds.length,
      strategy: meta?.strategy ?? 'fullSync',
    })

    emit({ type: 'sync:complete', entity: entityName, detail: { items: items.length, deleted: deletedIds.length } })
  } catch (error) {
    emit({ type: 'sync:error', entity: entityName, detail: { error: getErrorMessage(error) } })
    throw error
  }
}

// ─── Full Sync Orchestration ──────────────────────────────────────────────────

/**
 * Run a full sync cycle: flush outbox, then incremental sync for all offline entities.
 */
export async function runSyncCycle(entities: SyncManifestEntity[]): Promise<void> {
  if (isSyncing || !navigator.onLine) return

  isSyncing = true
  emit({ type: 'sync:start' })

  try {
    // Step 1: Flush outbox (mutations first, so server has latest data)
    await flushOutbox()

    // Step 2: Incremental sync per entity, ordered by priority
    const sorted = [...entities].sort((a, b) => {
      const pa = PRIORITY_ORDER[a.priority ?? 'normal'] ?? 1
      const pb = PRIORITY_ORDER[b.priority ?? 'normal'] ?? 1
      return pa - pb
    })

    for (const entity of sorted) {
      try {
        await incrementalSync(entity.name)
      } catch {
        // Individual entity sync failure should not stop others
      }
    }
  } finally {
    isSyncing = false
    await offlineDB.meta.put({ name: 'lastSync', value: new Date().toISOString() })
    emit({ type: 'sync:complete' })
  }
}

// ─── Lifecycle ────────────────────────────────────────────────────────────────

/**
 * Register sync event listeners (online/offline events, periodic sync).
 * Call on login after fetching the manifest.
 */
export function registerSyncListeners(entities: SyncManifestEntity[]): void {
  const handleOnline = () => {
    // Reconnected — run a full sync cycle
    runSyncCycle(entities)
  }

  window.addEventListener('online', handleOnline)

  // Periodic sync every 60s when online
  periodicInterval = setInterval(() => {
    if (navigator.onLine) {
      runSyncCycle(entities)
    }
  }, PERIODIC_SYNC_INTERVAL)

  // Store cleanup reference
  ;(registerSyncListeners as any)._cleanup = () => {
    window.removeEventListener('online', handleOnline)
    if (periodicInterval) {
      clearInterval(periodicInterval)
      periodicInterval = null
    }
  }
}

// ─── Outbox watchdog ──────────────────────────────────────────────────────────
// Minimal lifecycle that only drains the outbox — no manifest-driven sync.
// Intended for features like the GIS observation prototype that need POSTs to
// drain offline queues without depending on the full Phase 6 sync manifest.

const OUTBOX_WATCHDOG_INTERVAL = 20_000
let outboxWatchdogInterval: ReturnType<typeof setInterval> | null = null
let outboxWatchdogStarted = false

export function startOutboxWatchdog(): void {
  if (outboxWatchdogStarted) return
  outboxWatchdogStarted = true

  const tryFlush = () => {
    if (!navigator.onLine) return
    flushOutbox().catch(() => {
      /* swallow — outbox handles retries */
    })
  }

  window.addEventListener('online', tryFlush)
  window.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') tryFlush()
  })

  outboxWatchdogInterval = setInterval(tryFlush, OUTBOX_WATCHDOG_INTERVAL)

  // Opportunistic flush right away in case rows are already queued.
  tryFlush()
}

export function stopOutboxWatchdog(): void {
  if (outboxWatchdogInterval) {
    clearInterval(outboxWatchdogInterval)
    outboxWatchdogInterval = null
  }
  outboxWatchdogStarted = false
}

/**
 * Unregister sync listeners. Call on logout.
 */
export function unregisterSyncListeners(): void {
  const cleanup = (registerSyncListeners as any)._cleanup
  if (typeof cleanup === 'function') {
    cleanup()
    delete (registerSyncListeners as any)._cleanup
  }
}

/**
 * Initialize sync on login: register listeners, trigger initial sync for high-priority entities.
 */
export async function initializeOnLogin(entities: SyncManifestEntity[]): Promise<void> {
  registerSyncListeners(entities)

  if (navigator.onLine) {
    // Flush any pending outbox first
    await flushOutbox()

    // Sync high-priority entities first, then normal, then low
    const sorted = [...entities].sort((a, b) => {
      const pa = PRIORITY_ORDER[a.priority ?? 'normal'] ?? 1
      const pb = PRIORITY_ORDER[b.priority ?? 'normal'] ?? 1
      return pa - pb
    })

    for (const entity of sorted) {
      try {
        await incrementalSync(entity.name)
      } catch {
        // Non-critical: individual entity sync can fail
      }
    }
  }
}

/**
 * Handle logout: flush outbox if online, warn if offline with pending mutations, clear Dexie.
 */
export async function handleLogout(): Promise<{ pendingLost: number }> {
  unregisterSyncListeners()

  let pendingLost = 0

  if (navigator.onLine) {
    // Attempt to flush outbox before clearing
    await flushOutbox()
  }

  // Count remaining pending mutations (failed or offline)
  pendingLost = await offlineDB.outbox.count()

  // Clear all offline data
  await offlineDB.cache.clear()
  await offlineDB.outbox.clear()
  await offlineDB.syncMeta.clear()
  await offlineDB.conflicts.clear()
  await offlineDB.meta.clear()

  return { pendingLost }
}

/**
 * Handle reconnection: flush outbox, incremental sync, return stats.
 */
export async function handleReconnect(
  entities: SyncManifestEntity[]
): Promise<{ flushed: number; synced: number }> {
  const flushResult = await flushOutbox()

  let synced = 0
  for (const entity of entities) {
    try {
      await incrementalSync(entity.name)
      synced++
    } catch {
      // Individual sync failure is non-critical
    }
  }

  return { flushed: flushResult.processed, synced }
}

// ─── Status Queries ───────────────────────────────────────────────────────────

export async function getOfflineStatus() {
  const outboxCount = await offlineDB.outbox.count()
  const lastSync = await offlineDB.meta.get('lastSync')
  const conflictCount = await offlineDB.conflicts
    .filter(c => c.resolvedAt === null)
    .count()

  const failedCount = await offlineDB.outbox
    .filter(r => r.attempts >= r.maxAttempts)
    .count()

  return {
    isOffline: !navigator.onLine,
    isSyncing,
    pendingMutations: outboxCount,
    failedMutations: failedCount,
    conflictCount,
    lastSync: lastSync?.value ? new Date(lastSync.value) : null,
  }
}
